import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { APP_NAME } from "@/config";
import { useFlags } from "@/lib/flags";
import { Page, PageHeader, StatusBadge, PrivacyNote, Loading, usd, int, plural } from "@/components/common";
import { DeletionBadge } from "@/components/AccountDeletion";
import { cn } from "@/lib/utils";

// design_handoff_crewbench/screens/Companies.dc.html: every company with its first founder, plan (with the Store on),
// workspace server, people (founders + independent professionals), this month's sessions and spend.
const KIND = { standard_vm: "Standard cloud server", cloud_vm: "Cloud server", dedicated: "Dedicated server", local_demo: "Local demo server" };
const since = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "—");

export default function Companies() {
  const flags = useFlags();
  const [q, setQ] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["admin-companies"], queryFn: async () => (await api.get("/admin/companies")).data });
  const { data: runners } = useQuery({ queryKey: ["admin-runners"], queryFn: async () => (await api.get("/admin/runners")).data, refetchInterval: 15000 });
  const { data: store } = useQuery({ queryKey: ["store-companies"], enabled: !!flags.store, queryFn: async () => (await api.get("/admin/store/companies")).data });
  const search = (
    <input type="search" aria-label="Find a company or founder" placeholder="Find a company or founder" value={q} onChange={(e) => setQ(e.target.value)}
      className="w-[260px] max-w-full h-8 px-2.5 rounded-md border border-gr-control-border bg-transparent text-[13px] placeholder:text-muted-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring" />
  );
  const title = `Every company on ${APP_NAME}, with its server and spend.`;
  if (isLoading || !data) return <Page className="gap-5"><PageHeader eyebrow="Platform" title={title} actions={search} /><Loading /></Page>;

  const runnerOf = Object.fromEntries((runners?.runners || []).filter((r) => r.status !== "retired").map((r) => [r.company_id, r]));
  const credits = Object.fromEntries((store?.companies || []).map((c) => [c.id, c]));
  const companies = data.companies;
  const offline = companies.filter((c) => runnerOf[c.id]?.status === "offline").length;
  const without = companies.filter((c) => !c.runners && !c.deleted_at).length;
  const status = offline ? `${plural(offline, "workspace server")} ${offline === 1 ? "is" : "are"} offline.`
    : without ? `${plural(without, "company", "companies")} ${without === 1 ? "needs" : "need"} a workspace server.` : "Every workspace server is online.";
  const needle = q.trim().toLowerCase();
  const rows = companies.filter((c) => !needle || `${c.name} ${c.founder_email || ""}`.toLowerCase().includes(needle));
  const cols = cn("grid gap-3.5 px-4", flags.store
    ? "min-w-[900px] grid-cols-[minmax(0,1.6fr)_90px_minmax(0,1.3fr)_90px_90px_100px_110px_90px]"
    : "min-w-[800px] grid-cols-[minmax(0,1.6fr)_minmax(0,1.3fr)_90px_90px_100px_90px]");
  const creditsOf = (c) => {
    const s = credits[c.id];
    if (!s) return "—";
    return Object.values(s.billing || {}).includes("credits") ? usd(s.account?.balance_usd) : "Own keys";
  };
  return (
    <Page className="gap-5" data-testid="admin-companies">
      <PageHeader eyebrow={`Platform · ${plural(companies.length, "company", "companies")}`} title={title} status={status} actions={search} />
      <div className="bg-card rounded-[10px] overflow-x-auto text-sm tabular-nums">
        <div className={cn(cols, "py-2.5 border-b text-xs uppercase tracking-[0.09em] text-muted-foreground")}>
          <span>Company</span>{flags.store && <span>Plan</span>}<span>Workspace server</span><span className="text-right">People</span>
          <span className="text-right">Sessions</span><span className="text-right">Spent</span>
          {flags.store && <span className="text-right">Credits</span>}<span className="text-right">Since</span>
        </div>
        {rows.map((c) => {
          const r = runnerOf[c.id];
          return (
            <div key={c.id} className={cn(cols, "py-3 items-center border-b last:border-b-0")} data-testid={`company-row-${c.name}`}>
              <div className="flex flex-col min-w-0">
                <span className="text-base font-semibold [overflow-wrap:anywhere]">{c.name}<DeletionBadge deletion={c.deletion} deletedAt={c.deleted_at} /></span>
                {c.founder_email && <span className="text-muted-foreground [overflow-wrap:anywhere]">{c.founder_email}</span>}
              </div>
              {flags.store && <span>{c.plan || "No plan"}</span>}
              <span className="flex flex-wrap items-center gap-2">
                {r ? <><StatusBadge status={r.status} /><span className="text-muted-foreground">{KIND[r.kind] || r.kind}</span></>
                  : <span className="text-gr-warning" title="It can't start sessions until you add its runner (Runners)">Needs one</span>}
              </span>
              <span className="text-right" title={`${plural(c.founders, "founder")}, ${plural(c.team_members || 0, "team member")} and ${plural(c.coders - (c.team_members || 0), "independent professional")}`}>
                {int(c.founders)} + {int(c.coders)}
              </span>
              <span className="text-right">{int(c.sessions_month)}</span>
              <span className="text-right">{usd(c.monthly_spend_usd)}</span>
              {flags.store && <span className="text-right text-muted-foreground">{creditsOf(c)}</span>}
              <span className="text-right text-muted-foreground">{since(c.created_at)}</span>
            </div>
          );
        })}
        {rows.length === 0 && <div className="px-4 py-5 text-muted-foreground">No company matches.</div>}
      </div>
      <PrivacyNote>Platform admins see the same metadata founders do. Nobody here can read a session's prompts or replies.</PrivacyNote>
    </Page>
  );
}
